import { useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';

import GradientCircularProgress from '../../../../../components/common/GradientCircularProgress';
import { useThemeContext } from '../../../../../contexts/BaseThemeContextProvider';
import useBackendApi from '../../../../../hooks/useBackendApi';
import PrimaryBlueButton from '../../../../../components/common/buttons/PrimaryBlueButton';
import BorderedButton from '../../../../../components/common/buttons/BorderedButton';
import AccountEventForm, { AccountEventFormValues } from './AccountEventForm';
import { AccountEvent } from '../../../../../types/account';
import { hasAdminOrMemberRole } from './roleUtils';
import { buildAccountEventInitialValues, buildAccountEventPayload } from './eventPayloadUtils';

const backendURL = process.env.REACT_APP_BACKEND_URL as string;

type EditEventLocationState = {
  event?: AccountEvent;
  viewer_org_roles?: string[];
  viewer_account_roles?: string[];
};

const getViewerOrgRolesFromLocationState = (state: unknown): string[] => {
  const locationState = state as EditEventLocationState | null;
  const roles = locationState?.viewer_org_roles;
  return Array.isArray(roles) ? roles : [];
};

const getViewerAccountRolesFromLocationState = (state: unknown): string[] => {
  const locationState = state as EditEventLocationState | null;
  const roles = locationState?.viewer_account_roles;
  return Array.isArray(roles) ? roles : [];
};

export default function EditEventPage(): JSX.Element {
  const { org_path_name, acc_path_name, event_id } = useParams<{
    org_path_name: string;
    acc_path_name: string;
    event_id: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { theme } = useThemeContext();
  const { apiFetchRequest } = useBackendApi();

  const [viewerOrgRoles, setViewerOrgRoles] = useState<string[]>(() =>
    getViewerOrgRolesFromLocationState(location.state),
  );
  const [viewerAccountRoles, setViewerAccountRoles] = useState<string[]>(() =>
    getViewerAccountRolesFromLocationState(location.state),
  );
  const [initialValues, setInitialValues] = useState<Partial<AccountEventFormValues> | null>(null);
  const [formValues, setFormValues] = useState<AccountEventFormValues | null>(null);
  const [isValid, setIsValid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSupportDataLoading, setIsSupportDataLoading] = useState(true);
  const [errorText, setErrorText] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsSupportDataLoading(true);
  }, [event_id]);

  useEffect(() => {
    const fetchEventDetails = async () => {
      if (!org_path_name || !acc_path_name || !event_id) return;

      // Use location state as immediate initial value if available
      const locationState = location.state as EditEventLocationState | null;
      setViewerOrgRoles(getViewerOrgRolesFromLocationState(location.state));
      setViewerAccountRoles(getViewerAccountRolesFromLocationState(location.state));
      const stateEvent = locationState?.event;
      if (stateEvent) {
        setInitialValues(buildAccountEventInitialValues(stateEvent));
      }

      try {
        const res = await apiFetchRequest({
          endpoint: `${backendURL}/o/${encodeURIComponent(org_path_name)}/a/${encodeURIComponent(acc_path_name)}/events/${encodeURIComponent(event_id)}`,
          method: 'GET',
        });

        if (res && !res.isError) {
          const event = res.body.event as AccountEvent;
          setViewerOrgRoles(res.body.viewer_org_roles || []);
          setViewerAccountRoles(res.body.viewer_account_roles || []);
          setInitialValues(buildAccountEventInitialValues(event));
        } else {
          setErrorText('Failed to load event details.');
        }
      } catch {
        setErrorText('Failed to load event details.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchEventDetails();
  }, [acc_path_name, apiFetchRequest, event_id, location.state, org_path_name]);

  const canManageEvents = useMemo(() => {
    return hasAdminOrMemberRole(viewerOrgRoles) || hasAdminOrMemberRole(viewerAccountRoles);
  }, [viewerAccountRoles, viewerOrgRoles]);

  const canAssignStaff = useMemo(() => hasAdminOrMemberRole(viewerOrgRoles), [viewerOrgRoles]);
  const shouldShowAssignStaff = canAssignStaff;

  const handleSubmit = async () => {
    if (!formValues || !canManageEvents || !org_path_name || !acc_path_name || !event_id) return;
    setIsSubmitting(true);
    setErrorText(null);

    try {
      const response = await apiFetchRequest({
        endpoint: `${backendURL}/o/${encodeURIComponent(org_path_name)}/a/${encodeURIComponent(acc_path_name)}/events/${encodeURIComponent(event_id)}`,
        method: 'PUT',
        body: buildAccountEventPayload(formValues, {
          allowEmptyAssignedStaff: true,
          canAssignStaff,
        }),
      });

      if (response && !response.isError) {
        navigate(
          `/o/${encodeURIComponent(org_path_name)}/a/${encodeURIComponent(acc_path_name)}/events/${encodeURIComponent(event_id)}`,
        );
      } else {
        setErrorText(response?.body?.message || 'Failed to update event.');
      }
    } catch {
      setErrorText('An error occurred while updating the event.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading && !initialValues) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
        <GradientCircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: 4,
        maxWidth: '800px',
        mx: 'auto',
        backgroundColor: theme.layout.defaults.backgroundColor,
        borderRadius: '16px',
      }}
    >
      <Typography variant="h5" sx={{ mb: 4, fontWeight: 700 }}>
        Edit Event
      </Typography>

      {errorText && (
        <Typography color="error" sx={{ mb: 2 }}>
          {errorText}
        </Typography>
      )}

      {initialValues && (
        <AccountEventForm
          mode="edit"
          initialEventId={Number(event_id)}
          initialValues={initialValues}
          orgPathName={org_path_name!}
          accPathName={acc_path_name!}
          allowStaffAssignment={canAssignStaff}
          showStaffAssignment={shouldShowAssignStaff}
          onValuesChange={setFormValues}
          onValidationChange={setIsValid}
          onSupportDataLoadingChange={setIsSupportDataLoading}
          isTitleReadOnly={true}
        />
      )}

      <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <BorderedButton onClick={() => navigate(-1)} buttonText="Cancel" additionalProps={{ disabled: isSubmitting }} />
        <PrimaryBlueButton
          onClick={handleSubmit}
          buttonText="Update Event"
          isLoading={isSubmitting}
          additionalProps={{ disabled: !isValid || !canManageEvents || isLoading || isSupportDataLoading || isSubmitting }}
        />
      </Box>
    </Box>
  );
}
